import type { AppState } from '../../state/appState.js';
import type { ParamValue } from '../../graph/nodeDefs.js';
import {
  createDefaultGeometrySlot,
  createDefaultMaterialSlot,
  type LayerDef,
} from '../../scene/layerState.js';
import {
  createComponentEntityId,
  createLayerEntityId,
} from '../../ids/entityIds.js';
import type {
  Scene,
  SceneNodeId,
  FrameNode,
  InstanceNode,
  LayerNode,
  ComponentDef,
} from '../../scene/sceneState.js';
import {
  createFrameLayout,
  createFrameNode,
  createInstanceNode,
  createLayerNode,
} from '../../scene/sceneState.js';

export interface AddFrameCmd {
  type: 'scene:addFrame';
  frame: FrameNode;
  targetComponentId: string | null;
  parentId: SceneNodeId | null;
  index: number;
}

export interface AddLayerCmd {
  type: 'scene:addLayer';
  layer: LayerNode;
  layerDef: LayerDef;
  targetComponentId: string | null;
  parentId: SceneNodeId | null;
  index: number;
}

export interface AddInstanceCmd {
  type: 'scene:addInstance';
  instance: InstanceNode;
  component: ComponentDef;
  layerDef: LayerDef;
  targetComponentId: string | null;
  parentId: SceneNodeId | null;
  index: number;
}

export interface AddExistingInstanceCmd {
  type: 'scene:addExistingInstance';
  instance: InstanceNode;
  targetComponentId: string | null;
  parentId: SceneNodeId | null;
  index: number;
}

export interface DeleteSceneNodeCmd {
  type: 'scene:deleteNode';
  nodeId: SceneNodeId;
}

export type FrameEditableProps = Pick<
  FrameNode,
  | 'position'
  | 'rotation'
  | 'scale'
  | 'origin'
  | 'width'
  | 'height'
  | 'visible'
  | 'clip'
  | 'layout'
  | 'flexGrow'
  | 'flexShrink'
  | 'flexBasis'
  | 'alignSelf'
>;

export type InstanceEditableProps = Pick<
  InstanceNode,
  | 'position'
  | 'rotation'
  | 'scale'
  | 'origin'
  | 'visible'
  | 'fit'
  | 'width'
  | 'height'
  | 'flexGrow'
  | 'flexShrink'
  | 'flexBasis'
  | 'alignSelf'
>;

export type SetFramePropertyCmd = {
  [K in keyof FrameEditableProps]-?: {
    type: 'scene:setFrameProperty';
    nodeId: SceneNodeId;
    prop: K;
    value: FrameEditableProps[K];
  }
}[keyof FrameEditableProps];

export type SetInstancePropertyCmd = {
  [K in keyof InstanceEditableProps]-?: {
    type: 'scene:setInstanceProperty';
    nodeId: SceneNodeId;
    prop: K;
    value: InstanceEditableProps[K];
  }
}[keyof InstanceEditableProps];

/**
 * Set or clear one parameter override on a scene node.
 *
 * A node's override wins over the layer's own value at render time, so a panel
 * that edits the layer value leaves an overridden node untouched — the edit
 * lands and nothing moves. This is the command that edits the thing that wins.
 * `value: undefined` removes the override and lets the layer value through.
 */
export interface SetNodeParamOverrideCmd {
  type: 'scene:setNodeParamOverride';
  nodeId: SceneNodeId;
  key: string;
  value: ParamValue | undefined;
}

export interface RenameSceneNodeCmd {
  type: 'scene:rename';
  nodeId: SceneNodeId;
  name: string;
}

export interface RenameComponentCmd {
  type: 'component:rename';
  componentId: string;
  name: string;
}

export interface ClearAllCmd {
  type: 'app:clear';
}

export type ValueComponent = 0 | 1 | 2 | 3;
export type ValueRangeField = 'min' | 'max';

function resolveTargetScene(appState: AppState, parentId: SceneNodeId | null): { scene: Scene; targetComponentId: string | null } {
  const activeComponentId = appState.activeComponentId;
  if (activeComponentId) {
    const componentScene = appState.componentLibrary[activeComponentId]?.scene;
    if (componentScene && (parentId === null || componentScene.nodes[parentId])) {
      return { scene: componentScene, targetComponentId: activeComponentId };
    }
  }
  return { scene: appState.scene, targetComponentId: null };
}

export function createAddFrameCommand(appState: AppState, layout: 'row' | 'column' | 'stack' | null, parentId: SceneNodeId | null): AddFrameCmd {
  const { scene, targetComponentId } = resolveTargetScene(appState, parentId);
  const frame = createFrameNode({ name: 'Frame', layout: createFrameLayout(layout ?? 'free') });
  const index = parentId
    ? Math.max(0, scene.nodes[parentId]?.kind === 'frame' ? scene.nodes[parentId].childIds.length : scene.rootIds.length)
    : scene.rootIds.length;
  return { type: 'scene:addFrame', frame, targetComponentId, parentId, index };
}

export function createAddLayerCommand(appState: AppState, parentId: SceneNodeId | null): AddLayerCmd | null {
  const { scene, targetComponentId } = resolveTargetScene(appState, parentId);
  const layerId = createLayerEntityId();
  const layerDef: LayerDef = {
    id: layerId,
    name: 'Layer',
    geometry: createDefaultGeometrySlot(),
    material: createDefaultMaterialSlot(),
    builtIn: false,
  };
  const layer = createLayerNode(layerId, { name: 'Layer', fit: 'fill' });
  const index = parentId
    ? Math.max(0, scene.nodes[parentId]?.kind === 'frame' ? scene.nodes[parentId].childIds.length : scene.rootIds.length)
    : scene.rootIds.length;
  return { type: 'scene:addLayer', layer, layerDef, targetComponentId, parentId, index };
}

export function createAddInstanceCommand(appState: AppState, parentId: SceneNodeId | null): AddInstanceCmd {
  const { scene, targetComponentId } = resolveTargetScene(appState, parentId);
  const componentId = createComponentEntityId();
  const componentName = `Component ${Object.keys(appState.componentLibrary).length + 1}`;
  const layerId = createLayerEntityId();

  const layerDef: LayerDef = {
    id: layerId,
    name: 'Layer',
    geometry: createDefaultGeometrySlot(),
    material: createDefaultMaterialSlot(),
    builtIn: false,
  };

  const layerNode = createLayerNode(layerId, { name: 'Layer', fit: 'fill' });
  const rootFrame = createFrameNode({ name: 'Root', layout: createFrameLayout('stack') });
  rootFrame.childIds.push(layerNode.id);
  const component: ComponentDef = {
    id: componentId,
    name: componentName,
    scene: {
      rootIds: [rootFrame.id],
      nodes: {
        [rootFrame.id]: rootFrame,
        [layerNode.id]: layerNode,
      },
    },
  };

  const instance = createInstanceNode(componentId, { name: componentName });
  const index = parentId
    ? Math.max(0, scene.nodes[parentId]?.kind === 'frame' ? scene.nodes[parentId].childIds.length : scene.rootIds.length)
    : scene.rootIds.length;

  return { type: 'scene:addInstance', instance, component, layerDef, targetComponentId, parentId, index };
}

export function createAddExistingInstanceCommand(appState: AppState, componentId: string, parentId: SceneNodeId | null): AddExistingInstanceCmd | null {
  if (!appState.componentLibrary[componentId]) return null;
  const { scene, targetComponentId } = resolveTargetScene(appState, parentId);
  const componentName = appState.componentLibrary[componentId]?.name ?? 'Instance';
  const instance = createInstanceNode(componentId, { name: componentName });
  const index = parentId
    ? Math.max(0, scene.nodes[parentId]?.kind === 'frame' ? scene.nodes[parentId].childIds.length : scene.rootIds.length)
    : scene.rootIds.length;
  return { type: 'scene:addExistingInstance', instance, targetComponentId, parentId, index };
}

export function createDeleteSceneNodeCommand(nodeId: SceneNodeId): DeleteSceneNodeCmd {
  return { type: 'scene:deleteNode', nodeId };
}

export function createSetFramePropertyCommand<K extends keyof FrameEditableProps>(
  nodeId: SceneNodeId,
  prop: K,
  value: FrameEditableProps[K],
): Extract<SetFramePropertyCmd, { prop: K }> {
  return { type: 'scene:setFrameProperty', nodeId, prop, value } as Extract<SetFramePropertyCmd, { prop: K }>;
}

export function createSetInstancePropertyCommand<K extends keyof InstanceEditableProps>(
  nodeId: SceneNodeId,
  prop: K,
  value: InstanceEditableProps[K],
): Extract<SetInstancePropertyCmd, { prop: K }> {
  return { type: 'scene:setInstanceProperty', nodeId, prop, value } as Extract<SetInstancePropertyCmd, { prop: K }>;
}

export function createSetNodeParamOverrideCommand(
  nodeId: SceneNodeId,
  key: string,
  value: ParamValue | undefined,
): SetNodeParamOverrideCmd {
  return { type: 'scene:setNodeParamOverride', nodeId, key, value };
}

export function createRenameSceneNodeCommand(nodeId: SceneNodeId, name: string): RenameSceneNodeCmd {
  return { type: 'scene:rename', nodeId, name };
}

export function createRenameComponentCommand(componentId: string, name: string): RenameComponentCmd {
  return { type: 'component:rename', componentId, name };
}

export function createClearCommand(): ClearAllCmd {
  return { type: 'app:clear' };
}
